import { axiosClient, axiosClientCloud } from "../AxiosClient";

/**
 * Capa de acceso a datos del módulo Gestión Cartera.
 * Todas las pantallas y modales consumen la API SOLO a través de este archivo,
 * así la integración con backend se hace en un único lugar.
 */

export const PAGE_SIZE = 20;

const getUserUuid = () => localStorage.getItem("userUuid");

export const SEARCH_MODES = { contains: "contains", exact: "exact" };

/** Quita filtros vacíos para no enviar parámetros en blanco al backend. */
const withoutEmpty = (params) =>
  Object.fromEntries(Object.entries(params).filter(([, value]) => value !== "" && value !== null && value !== undefined));

/**
 * Listado de clientes con métricas de cartera (GET /v1/portfolio/clients). Filtro de mora, orden y
 * búsqueda (`searchMode`: contains al escribir, exact con el botón Buscar) se resuelven en backend.
 */
export const fetchPortfolioClients = async ({ page, query, mode, moraFilter, order }) => {
  const response = await axiosClient.get("/v1/portfolio/clients", {
    params: withoutEmpty({ page, size: PAGE_SIZE, query: query.trim(), searchMode: mode, moraFilter, order }),
  });
  return { rows: response.data.content, totalElements: response.data.totalElements };
};

export const fetchClient = async (clientUuid) => {
  const response = await axiosClient.get(`/v1/clients/${clientUuid}`);
  return response.data;
};

/** Créditos del cliente, cada uno con todas sus cuotas (CreditWithQuotas). */
export const fetchClientCreditsWithQuotas = async (clientUuid) => {
  const response = await axiosClient.get(`/v1/quotas/client/${clientUuid}`);
  return response.data;
};

export const fetchCredit = async (creditUuid) => {
  const response = await axiosClient.get(`/v1/credits/${creditUuid}`);
  return response.data;
};

/** Saldo a pagar por componente (capital, mora, GAC, etc.), el mismo cálculo de balanceCredit. */
export const fetchCreditPayoffBreakdown = async (creditUuid) => {
  const response = await axiosClient.get(`/v1/credits/${creditUuid}/payoff-breakdown`);
  return response.data;
};

/** Cuotas del crédito con el GAC de cada una. El listado de cuotas de cartera no trae ese campo. */
export const fetchCreditQuotaStatements = async (creditUuid) => {
  const response = await axiosClient.get(`/v1/condonations/credits/${creditUuid}/quotas/all`);
  return response.data;
};

/** El endpoint es paginado (Page de Spring): se piden hasta 50 referencias y se devuelve la lista. */
const MAX_REFERENCES = 50;

export const fetchClientReferences = async (clientUuid) => {
  const response = await axiosClient.get(`/v1/client-references/client/${clientUuid}`, {
    params: { size: MAX_REFERENCES },
  });
  return response.data.content;
};

export const createClientReference = (clientReferenceRequest) =>
  axiosClient.post("/v1/client-references", clientReferenceRequest);

export const updateClientReference = (referenceUuid, clientReferenceRequest) =>
  axiosClient.patch(`/v1/client-references/${referenceUuid}`, clientReferenceRequest);

/** El historial muestra las últimas 10 gestiones; el resto se trae con "Cargar más". */
export const COLLECTION_PAGE_SIZE = 10;

/**
 * Historial de gestiones de cobranza del cliente (todas las de todos sus créditos), normalizado por
 * backend, paginado, filtrado y del más reciente al más antiguo. `filters`: { creditUuid, result, channel, from, to }.
 */
export const fetchCollectionHistory = async ({ clientUuid, filters, page }) => {
  const response = await axiosClient.get(`/v1/collectionManagement/client/${clientUuid}`, {
    params: withoutEmpty({ page, size: COLLECTION_PAGE_SIZE, ...filters }),
  });
  return response.data;
};

/** El historial de pagos muestra los últimos 10 pagos; el resto se trae con "Cargar más". */
export const PAYMENT_HISTORY_PAGE_SIZE = 10;

/**
 * Historial de pagos (payment_records) del cliente: los pagos con su documento + los aplicados a sus
 * créditos, del más reciente al más antiguo. `params`: salida de `toPaymentHistoryParams`.
 */
export const fetchClientPayments = async ({ clientUuid, params, page, size = PAYMENT_HISTORY_PAGE_SIZE }) => {
  const response = await axiosClient.get(`/v1/portfolio/clients/${clientUuid}/payments`, {
    params: withoutEmpty({ page, size, ...params }),
  });
  return response.data;
};

/** Payment records del crédito, para completar oldId y notas de los pagos aplicados a una cuota. */
export const fetchCreditPaymentRecords = async (clientUuid, creditUuid) => {
  const size = 100;
  const records = [];
  let page = 0;
  let totalPages = 1;
  while (page < totalPages && page < 20) {
    const response = await fetchClientPayments({ clientUuid, params: { creditUuid }, page, size });
    records.push(...(response.content || []));
    totalPages = response.totalPages || 1;
    page += 1;
  }
  return records;
};

/**
 * Transcripción (y grabación, si existe) de una llamada de IA. Mientras backend no tenga la fuente
 * definida responde `available: false` y `turns` vacío.
 */
export const fetchCallTranscript = async (externalCallId) => {
  const response = await axiosClient.get(`/v1/collectionManagement/calls/${externalCallId}/transcript`);
  return response.data;
};

export const fetchClientForEdit = async (clientUuid) => {
  const response = await axiosClientCloud.get(`/v1/clients/${clientUuid}`);
  return response.data;
};

export const updateClient = (clientUuid, clientRequest) =>
  axiosClientCloud.patch(`/v1/clients/${clientUuid}`, clientRequest, {
    headers: { "Content-Type": "application/json" },
  });

export const fetchImageUrl = async (key, extension = ".jpg") => {
  const response = await axiosClient.get(`/v1/s3/url/${key}${extension}`);
  return response.data;
};

export const fetchPdfUrl = async (key) => {
  const response = await axiosClient.get(`/v1/s3/url/pdf/${key}.pdf`);
  return response.data;
};

/** La factura guarda la llave completa, sin extensión aparte. */
export const fetchStoredFileUrl = async (key) => {
  const response = await axiosClient.get(`/v1/s3/url/${key}`);
  return response.data;
};

/**
 * Todos los créditos del cliente con la factura del comercio de cada uno (`invoiceName`, `facturaEnviada`)
 * y si se le puede cargar (`invoiceable`), del originado más reciente al más antiguo.
 */
export const fetchClientCreditInvoices = async (clientUuid) => {
  const response = await axiosClient.get(`/v1/portfolio/clients/${clientUuid}/credit-invoices`);
  return response.data;
};

/** Factura del crédito elegido (POST /v1/portfolio/clients/{clientUuid}/credits/{creditUuid}/invoice). */
export const uploadCreditInvoice = (clientUuid, creditUuid, file) => {
  const form = new FormData();
  form.append("image", file);
  return axiosClient.post(`/v1/portfolio/clients/${clientUuid}/credits/${creditUuid}/invoice`, form);
};

/** Detalle de la cuota (GET /v1/quotas/{quotaUuid}): GAC, mora, condonación y overpayment actualizados. */
export const fetchQuota = async (quotaUuid) => {
  const response = await axiosClient.get(`/v1/quotas/${quotaUuid}`);
  return response.data;
};

/**
 * Componentes proyectados (saldo pendiente por componente) de una cuota sin pagar.
 * Endpoint REAL del módulo de condonaciones: QuotaPendingBreakdownDto.
 */
export const fetchQuotaProjectedComponents = async (quotaUuid) => {
  const response = await axiosClient.get(`/v1/condonations/quotas/${quotaUuid}/pending`);
  return response.data;
};

/** Componentes efectivamente pagados de una cuota (suma de sus aplicaciones de pago válidas). */
export const fetchQuotaPaidComponents = async (quotaUuid) => {
  const response = await axiosClient.get(`/v1/quotas/${quotaUuid}/paid-components`);
  return response.data;
};

/** Plan de pagos original del crédito: lo que se estimaba en la originación, por cuota y componente. */
export const fetchOriginalPaymentPlan = async (creditUuid) => {
  const response = await axiosClient.get(`/v1/credits/${creditUuid}/original-payment-plan`);
  return response.data;
};

export const fetchParameters = async () => {
  const response = await axiosClient.get("/v1/parameters");
  return response.data;
};

export const fetchRestructuringQuotaValue = async (valueOptionRequest) => {
  const response = await axiosClient.post("/v1/quotas/valueOption", valueOptionRequest);
  return response.data;
};

export const restructureCredit = (creditReestructuring) =>
  axiosClient.post("/v1/credits/restructuring", creditReestructuring);

/**
 * Cambia el día de pago. El endpoint recibe el uuid del CLIENTE: si el cliente tiene
 * varios créditos, backend decide cuál actualiza (QuotaServiceImpl.updateDateQuotas).
 */
export const updateClientPayDay = (clientUuid, dayOfMonth) =>
  axiosClient.put(`/v1/quotas/update/${clientUuid}/${dayOfMonth}`);

const buildCsvForm = (file) => {
  const csv = new FormData();
  csv.append("file", file);
  return csv;
};

/** Acciones del modal "Importar" (CSV). Mismos endpoints que el módulo Cobranza. */
export const IMPORT_ACTIONS = {
  payQuotas: (file) => axiosClient.post(`/v1/quotas/import/${getUserUuid()}`, buildCsvForm(file)),
  deleteQuotas: (file) =>
    axiosClient.post(`/v1/quotas/delete/import/${getUserUuid()}`, buildCsvForm(file)),
  deletePaymentRecord: (file) =>
    axiosClient.post(
      `v1/payment-record/eliminate-payment-record/${getUserUuid()}`,
      buildCsvForm(file)
    ),
  registerCollection: (file) =>
    axiosClient.post("/v1/collectionManagement/import", buildCsvForm(file)),
  commitmentDate: (file) =>
    axiosClient.post("/v1/quotas/import/update-quotas", buildCsvForm(file)),
  paymentApplier: (file) =>
    axiosClient.post(`v1/payment-record/import/${getUserUuid()}`, buildCsvForm(file), {
      responseType: "blob",
    }),
  applicationAdjustment: (file) =>
    axiosClient.post("/v1/payment-record/import-adjustments", buildCsvForm(file)),
};

/** Importadores del antiguo módulo Clientes. */
export const importClientsData = (file) =>
  axiosClient.post("/v1/importer/clients/update-data", buildCsvForm(file), {
    headers: { "Content-Type": "multipart/form-data" },
  });

export const importViabilitiesStudy = (file) =>
  axiosClient.post("/v1/importer/viabilities-study", buildCsvForm(file));
